import { useEffect, useState } from "react";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, HelpCircle, Plug, Save, ShieldCheck, Wrench, XCircle } from "lucide-react";
import { PERM, hasGlobalPermission } from "@/lib/permissions";

const SOURCE_LABEL = {
  settings: "saved here",
  env: "from environment",
  unset: "not set",
};

/**
 * Credentials of the Keycloak Admin API client (the dedicated confidential
 * client that provisions users, realm roles and /sites/* groups — see
 * backend/keycloak_admin.py). System Administrator only, enforced by the
 * backend on every call; the card renders nothing for anyone else.
 *
 * Values saved here take precedence over the KEYCLOAK_ADMIN_CLIENT_ID/_SECRET
 * environment variables. The secret is write-only: the backend never returns
 * it, so the field stays empty and is only sent when a new one is typed.
 * The issuer is the SSO login setting (OIDC_ISSUER) and is shown read-only.
 */
export function KeycloakSettingsCard() {
  const { user } = useAuth();
  const allowed = hasGlobalPermission(user, PERM.SYSTEM_KEYCLOAK_MANAGE);

  const [info, setInfo] = useState(null);
  const [clientId, setClientId] = useState("");
  const [secret, setSecret] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [changeUrl, setChangeUrl] = useState("");
  const [resetUrl, setResetUrl] = useState("");
  const [realmChecks, setRealmChecks] = useState(null);
  const [realmBusy, setRealmBusy] = useState(false);

  const apply = (data) => {
    setInfo(data);
    setClientId(data.client_id || "");
    setChangeUrl(data.change_password_url || "");
    setResetUrl(data.reset_password_url || "");
    setSecret("");
  };

  useEffect(() => {
    if (!allowed) return;
    (async () => {
      try {
        const { data } = await client.get("/admin/keycloak-settings");
        apply(data);
        // Read-only realm check on load, so a misconfigured realm is visible
        // without anyone having to click Check.
        if (data.configured) checkRealm(false, true);
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || "Could not load Keycloak settings");
      } finally {
        setLoading(false);
      }
    })();
  }, [allowed]);

  if (!allowed) return null;

  const save = async () => {
    const body = {};
    if (clientId.trim() !== (info?.client_id || "")) body.client_id = clientId.trim();
    if (secret.trim()) body.client_secret = secret.trim();
    if (changeUrl.trim() !== (info?.change_password_url || "")) body.change_password_url = changeUrl.trim();
    if (resetUrl.trim() !== (info?.reset_password_url || "")) body.reset_password_url = resetUrl.trim();
    if (Object.keys(body).length === 0) {
      toast.info("Nothing to save");
      return;
    }
    setSaving(true);
    try {
      const { data } = await client.put("/admin/keycloak-settings", body);
      apply(data);
      toast.success("Keycloak settings saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      await client.post("/admin/keycloak-settings/test");
      toast.success("Connected — Keycloak issued an admin token");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Connection failed");
    } finally {
      setTesting(false);
    }
  };

  const checkRealm = async (apply = false, quiet = false) => {
    setRealmBusy(true);
    try {
      const { data } = apply
        ? await client.post("/admin/keycloak-settings/realm-config/apply")
        : await client.get("/admin/keycloak-settings/realm-config", { params: { refresh: !quiet } });
      setRealmChecks(data.checks);
      if (apply) toast.success("Realm configuration applied");
    } catch (e) {
      if (!quiet) toast.error(formatApiError(e?.response?.data?.detail) || "Realm configuration request failed");
    } finally {
      setRealmBusy(false);
    }
  };

  const disabled = loading || saving;

  return (
    <Card className="mb-6 p-6" data-testid="settings-keycloak-card">
      <div className="mb-1 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-lg font-semibold">Keycloak Settings</h2>
        <span className="ml-1 rounded-full border border-border bg-muted px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest">
          System Administrator only
        </span>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        Credentials of the Keycloak admin client used to provision users, realm roles and site groups. Values saved
        here override the <span className="font-mono">KEYCLOAK_ADMIN_CLIENT_ID</span> /{" "}
        <span className="font-mono">KEYCLOAK_ADMIN_CLIENT_SECRET</span> environment variables.
      </p>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <Label className="data-label mb-1 block">Issuer URL</Label>
          <Input
            data-testid="settings-keycloak-issuer"
            value={info?.issuer || ""}
            readOnly
            disabled
            placeholder="OIDC_ISSUER is not set"
            className="font-mono text-xs"
          />
          <p className="mt-1 text-[10px] text-muted-foreground">
            Shared with SSO login (<span className="font-mono">OIDC_ISSUER</span>) — change it in the environment.
          </p>
        </div>
        <div>
          <Label className="data-label mb-1 block">Admin client ID</Label>
          <Input
            data-testid="settings-keycloak-client-id"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            placeholder="ddm-admin"
            disabled={disabled}
            autoComplete="off"
            className="font-mono text-xs"
          />
          <p className="mt-1 text-[10px] text-muted-foreground">
            Currently {SOURCE_LABEL[info?.source?.client_id || "unset"]}.
          </p>
        </div>
        <div>
          <Label className="data-label mb-1 block">Admin client secret</Label>
          <Input
            data-testid="settings-keycloak-client-secret"
            type="password"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder={info?.client_secret_set ? "•••••••• (leave blank to keep)" : "Not set"}
            disabled={disabled}
            autoComplete="new-password"
            className="font-mono text-xs"
          />
          <p className="mt-1 text-[10px] text-muted-foreground">
            Currently {SOURCE_LABEL[info?.source?.client_secret || "unset"]}. Never shown again once saved.
          </p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 border-t border-border pt-5 md:grid-cols-2">
        <div className="md:col-span-2">
          <p className="data-label">Password pages</p>
          <p className="mt-1 text-[10px] text-muted-foreground">
            Where users are sent to change or reset their password (Profile page, welcome and reset emails). Leave blank
            to use the default.
          </p>
        </div>
        <div className="md:col-span-2">
          <Label className="data-label mb-1 block">Change password URL</Label>
          <Input
            data-testid="settings-keycloak-change-url"
            value={changeUrl}
            onChange={(e) => setChangeUrl(e.target.value)}
            placeholder={info?.change_password_url_default || ""}
            disabled={disabled}
            className="font-mono text-xs"
          />
          <p className="mt-1 text-[10px] text-muted-foreground">
            Default signs the user in through Keycloak, opens its Update Password screen and returns to the homepage.
          </p>
        </div>
        <div className="md:col-span-2">
          <Label className="data-label mb-1 block">Reset (forgot) password URL</Label>
          <Input
            data-testid="settings-keycloak-reset-url"
            value={resetUrl}
            onChange={(e) => setResetUrl(e.target.value)}
            placeholder={info?.reset_password_url_default || ""}
            disabled={disabled}
            className="font-mono text-xs"
          />
        </div>
      </div>

      <div className="mt-6 border-t border-border pt-5" data-testid="settings-keycloak-realm-config">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="data-label">Realm configuration</p>
            <p className="mt-1 text-[10px] text-muted-foreground">
              One-time realm setup for new environments: new users must change their temporary password, "Forgot
              password" is enabled, and only admins can edit usernames and emails. Applying needs the <span className="font-mono">manage-realm</span> role on the admin
              client.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => checkRealm(false)} disabled={realmBusy || !info?.configured}>
              Check
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => checkRealm(true)}
              disabled={realmBusy || !info?.configured}
              data-testid="settings-keycloak-apply-realm"
            >
              <Wrench className="h-3.5 w-3.5" />
              Apply
            </Button>
          </div>
        </div>
        {realmChecks && (
          <ul className="mt-3 space-y-1.5 text-xs">
            {realmChecks.map((c) => (
              <li key={c.key} className="flex items-start gap-2">
                {c.ok === true && <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />}
                {c.ok === false && <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />}
                {c.ok == null && <HelpCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />}
                <span>
                  {c.label}
                  {c.detail && <span className="mt-0.5 block text-[10px] text-muted-foreground">{c.detail}</span>}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {info && !info.configured && (
        <p className="mt-4 text-[11px] text-destructive" data-testid="settings-keycloak-unconfigured">
          Keycloak admin access is not fully configured — user, role and group sync is skipped until the issuer,
          client ID and secret are all set.
        </p>
      )}
      {info?.group_sync_disabled && (
        <p className="mt-2 text-[11px] text-muted-foreground">
          Role and group sync is switched off by <span className="font-mono">KEYCLOAK_GROUP_SYNC_DISABLED</span>.
        </p>
      )}

      <div className="mt-6 flex justify-end gap-2">
        <Button
          data-testid="settings-keycloak-test"
          variant="outline"
          onClick={testConnection}
          disabled={disabled || testing || !info?.configured}
          className="gap-1.5"
        >
          <Plug className="h-3.5 w-3.5" />
          Test connection
        </Button>
        <Button data-testid="settings-keycloak-save" onClick={save} disabled={disabled} className="gap-1.5">
          <Save className="h-3.5 w-3.5" />
          Save
        </Button>
      </div>
    </Card>
  );
}
